export interface ShiftsWhereOptions {
    fromVar?: string; // default '$from'
    toVar?: string;   // default '$to'
    workplaceIdVar?: string; // e.g. '$workplaceId'
    locationIdVar?: string;  // e.g. '$locationId'
}

export function buildShiftsWhere(opts: ShiftsWhereOptions = {}): string {
    const fromVar = opts.fromVar ?? '$from';
    const toVar = opts.toVar ?? '$to';
    const parts: string[] = [
        `fromDate: { gte: ${fromVar} }`,
        `toDate: { lte: ${toVar} }`,
    ];
    if (opts.workplaceIdVar) parts.push(`workplaceId: { eq: ${opts.workplaceIdVar} }`);
    if (opts.locationIdVar) parts.push(`workplace: { locationId: { eq: ${opts.locationIdVar} } }`);
    return `{ ${parts.join(', ')} }`;
}

export interface PlanItemsWhereOptions {
    fromVar?: string; // default '$from'
    toVar?: string;   // default '$to'
    locationIds?: string[];
    workplaceIds?: string[];
    projectIds?: string[];
    shiftTypeIds?: string[];
    shiftTeamTypeIds?: string[];
    faultTypeIds?: string[];
}

export function buildPlanItemsWhere(opts: PlanItemsWhereOptions = {}): string {
    const fromVar = opts.fromVar ?? '$from';
    const toVar = opts.toVar ?? '$to';
    const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
    const parts: string[] = [
        `isWorkplaceShutdown: { eq: true }`,
        `auditFaultType: { isPlannedShutdown: { eq: false } }`,
        `shutdownFromDate: { gte: ${fromVar} }`,
        `shutdownToDate: { lte: ${toVar} }`,
    ];
    if (opts.workplaceIds && opts.workplaceIds.length > 0) parts.push(`workplaceId: { in: ${listExpr(opts.workplaceIds)} }`);
    if (opts.locationIds && opts.locationIds.length > 0) parts.push(`workplace: { locationId: { in: ${listExpr(opts.locationIds)} } }`);
    if (opts.faultTypeIds && opts.faultTypeIds.length > 0) parts.push(`auditFaultTypeId: { in: ${listExpr(opts.faultTypeIds)} }`);
    if (opts.projectIds && opts.projectIds.length > 0) parts.push(`shift: { shiftProductions: { some: { projectId: { in: ${listExpr(opts.projectIds)} } } } }`);
    if (opts.shiftTypeIds && opts.shiftTypeIds.length > 0) parts.push(`shift: { shiftTypeId: { in: ${listExpr(opts.shiftTypeIds)} } }`);
    if (opts.shiftTeamTypeIds && opts.shiftTeamTypeIds.length > 0) parts.push(`shift: { shiftTeamTypeId: { in: ${listExpr(opts.shiftTeamTypeIds)} } }`);
    return `{ ${parts.join(', ')} }`;
}

export interface ShiftScrapRecordsWhereOptions {
    fromVar?: string;
    toVar?: string;
    locationIds?: string[];
    workplaceIds?: string[];
    projectIds?: string[];
    shiftTypeIds?: string[];
    shiftTeamTypeIds?: string[];
}

export function buildShiftScrapRecordsWhere(opts: ShiftScrapRecordsWhereOptions = {}): string {
    const fromVar = opts.fromVar ?? '$from';
    const toVar = opts.toVar ?? '$to';
    const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
    const parts: string[] = [
        `recordTime: { gte: ${fromVar}, lte: ${toVar} }`,
    ];
    if (opts.workplaceIds && opts.workplaceIds.length > 0) parts.push(`station: { workplaceId: { in: ${listExpr(opts.workplaceIds)} } }`);
    if (opts.locationIds && opts.locationIds.length > 0) parts.push(`station: { workplace: { locationId: { in: ${listExpr(opts.locationIds)} } } }`);
    if (opts.projectIds && opts.projectIds.length > 0) parts.push(`shift: { shiftProductions: { some: { projectId: { in: ${listExpr(opts.projectIds)} } } } }`);
    if (opts.shiftTypeIds && opts.shiftTypeIds.length > 0) parts.push(`shift: { shiftTypeId: { in: ${listExpr(opts.shiftTypeIds)} } }`);
    if (opts.shiftTeamTypeIds && opts.shiftTeamTypeIds.length > 0) parts.push(`shift: { shiftTeamTypeId: { in: ${listExpr(opts.shiftTeamTypeIds)} } }`);
    return `{ ${parts.join(', ')} }`;
}

export interface PanelDashboardPlanItemsWhereOptions {
    workplaceIds?: string[];
    locationIds?: string[];
}

export function buildPanelDashboardPlanItemsWhere(opts: PanelDashboardPlanItemsWhereOptions): string {
    const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
    
    // Základní části: processType = IssueSolving AND time <= referenceDateTime
    const parts: string[] = [
        `processType: { eq: ISSUE_SOLVING }`,
        `time: { lte: $referenceDateTime }`,
    ];
    
    // Vnořená podmínka: planItemState = 'WaitingToPerform' OR (shutdown podmínky)
    const orConditions: string[] = [
        `{ planItemState: { eq: WAITING_TO_PERFORM } }`,
        `{ 
            and: [
                { isWorkplaceShutdown: { eq: true } },
                { shutdownFromDate: { lte: $referenceDateTime } },
                { or: [
                    { shutdownToDate: { eq: null } },
                    { shutdownToDate: { gte: $referenceDateTime } }
                ] }
            ]
        }`,
    ];
    
    parts.push(`or: [${orConditions.join(', ')}]`);
    
    // Přidání filtrů dle workplaceIds nebo locationIds
    if (opts.workplaceIds && opts.workplaceIds.length > 0) {
        parts.push(`workplaceId: { in: ${listExpr(opts.workplaceIds)} }`);
    }
    if (opts.locationIds && opts.locationIds.length > 0) {
        parts.push(`workplace: { locationId: { in: ${listExpr(opts.locationIds)} } }`);
    }
    
    return `{ ${parts.join(', ')} }`;
}


